import { isValid, parseISO } from "date-fns";
import { FinancialAccountType } from "@common/types/financialAccount";
import { ViewSchema, ViewType, type View } from "@common/types/views/views";

export function defaultDocumentRetrievalCloseTemplateView(): View {
    return {
        type: ViewType.FINANCIAL_DOCUMENT,
        columns: [],
        filters: {},
    };
}

export function parseDocumentRetrievalCloseTemplateViewConfig(
    raw: Record<string, unknown> | null,
): View {
    if (!raw) {
        return defaultDocumentRetrievalCloseTemplateView();
    }
    const parsed = ViewSchema.safeParse(raw);
    if (parsed.success && parsed.data.type === ViewType.FINANCIAL_DOCUMENT) {
        return parsed.data;
    }
    return defaultDocumentRetrievalCloseTemplateView();
}

export function serializeDocumentRetrievalCloseTemplateViewConfig(
    view: View,
): Record<string, unknown> {
    return view as unknown as Record<string, unknown>;
}

function isKnownFinancialAccountType(
    value: string,
): value is FinancialAccountType {
    return Object.values(FinancialAccountType).some((type) => type === value);
}

export function readDocumentRetrievalFinancialAccountType(
    raw: Record<string, unknown> | null,
): FinancialAccountType | null {
    if (!raw) {
        return null;
    }
    const v = raw.financialAccountType;
    // Keep types the picker no longer offers (loan, investment, line of
    // credit). Dropping them here makes the editor save "any account type"
    // and the next apply creates an unscoped task.
    return typeof v === "string" && isKnownFinancialAccountType(v) ? v : null;
}

/**
 * Reads the specific financial account IDs stored on a client-specific
 * document-retrieval template task (mutually exclusive with
 * `financialAccountType` — selecting specific accounts overrides the
 * account-type filter). Only meaningful for workspace-scoped templates,
 * since account IDs are workspace-specific.
 */
export function readDocumentRetrievalFinancialAccountIds(
    raw: Record<string, unknown> | null,
): number[] {
    if (!raw) {
        return [];
    }
    const v = raw.financialAccountIds;
    if (!Array.isArray(v)) {
        return [];
    }
    return v.filter((item): item is number => typeof item === "number");
}

function serializeDocumentRetrievalCloseTemplateViewConfigWithAccountType(
    view: View,
    financialAccountType: FinancialAccountType | null,
    financialAccountIds: number[],
): Record<string, unknown> {
    const strippedFilters = { ...view.filters };
    delete strippedFilters.periodStart;
    delete strippedFilters.periodEnd;
    delete strippedFilters.accountIds;
    delete strippedFilters.documentCategoryIds;
    const strippedView: View = { ...view, filters: strippedFilters };
    const base =
        serializeDocumentRetrievalCloseTemplateViewConfig(strippedView);
    if (financialAccountIds.length > 0) {
        return { ...base, financialAccountIds };
    }
    if (financialAccountType != null) {
        return { ...base, financialAccountType };
    }
    return base;
}

function readDateOverrideField(
    raw: Record<string, unknown> | null,
    fieldName: string,
): string | null {
    if (!raw) {
        return null;
    }
    const v = raw[fieldName];
    if (typeof v !== "string") {
        return null;
    }
    return isValid(parseISO(v)) ? v : null;
}

export function readDocumentRetrievalStartDateOverride(
    raw: Record<string, unknown> | null,
): string | null {
    return readDateOverrideField(raw, "startDateOverride");
}

export function readDocumentRetrievalEndDateOverride(
    raw: Record<string, unknown> | null,
): string | null {
    return readDateOverrideField(raw, "endDateOverride");
}

interface DocumentRetrievalViewOptions {
    financialAccountType: FinancialAccountType | null;
    /** Specific account IDs (client-specific templates only); takes precedence over `financialAccountType` when non-empty. */
    financialAccountIds: number[];
    startDateOverride: string | null;
    endDateOverride: string | null;
}

function normalizeISODateValue(value: string | null): string | null {
    if (value == null || value === "") {
        return null;
    }
    return isValid(parseISO(value)) ? value : null;
}

export function serializeDocumentRetrievalCloseTemplateViewConfigWithDates(
    view: View,
    options: DocumentRetrievalViewOptions,
): Record<string, unknown> {
    const base =
        serializeDocumentRetrievalCloseTemplateViewConfigWithAccountType(
            view,
            options.financialAccountType,
            options.financialAccountIds,
        );
    const startDateOverride = normalizeISODateValue(options.startDateOverride);
    const endDateOverride = normalizeISODateValue(options.endDateOverride);
    return {
        ...base,
        ...(startDateOverride != null ? { startDateOverride } : {}),
        ...(endDateOverride != null ? { endDateOverride } : {}),
    };
}
